import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { CarsIcon, Clockicon, CupIcon, NextArrow } from "../../assets/icons";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import WorkerProfile from "./WorkerProfile";
import AddProjectSidebar from "../../components/common/AddProjectSideBar";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getWorkerDetail, getExpenses } from "../../services/service";
import { Modal } from "@mui/material";
import DailyStatsCalendar from "./DailyStatsCalendar";
import DailyStats from "./DailyStats";
import { Button, Spin } from "antd";
import SearchMap from "../../components/common/SearchMap";
import { useSelector } from 'react-redux';
import { selectCurrentPlan } from '../../redux/selectors/authSelectors';
import UpgradePromptModal from '../../components/common/UpgradePromptModal';
import { FEATURE_REQUIRED_PLANS } from '../../utils/planFeatureAccess';
import WorkerHoursReport from './WorkerHoursReport';

const WorkerDetails = () => {
  const [workerData, setWorkerData] = useState([]);
  const [isProjectbarVisible, setProjectbarVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [loader, setLoader] = useState(true);
  const location = useLocation();
  const { id } = useParams();
  const stateWorkerId = location.state?.workerId;
  const workerId = id || stateWorkerId;
  const [filter, setFilter] = useState("This Year"); 
  const [expenses, setExpenses] = useState([]);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [restrictedFeature, setRestrictedFeature] = useState(null);
  const currentPlan = useSelector(selectCurrentPlan);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const formatTime = (timeString) => {
    const [hours, seconds] = timeString.split(':').map(Number);
    
    let formattedTime = '';
    
    // Convert seconds to minutes and remaining seconds
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      formattedTime += `${hours}hrs `;
    }
    
    if (minutes > 0) {
      formattedTime += `${minutes}mins `;
    }
    
    if (remainingSeconds > 0) {
      formattedTime += `${remainingSeconds}sec`;
    }
    
    // If all values are 0, or if the time string is empty/invalid
    if (!formattedTime) {
      return '0hrs';
    }
    
    return formattedTime.trim();
  };

  const getTimeStats = (activity) => {
    const stat = workerData?.time_stats?.find(
      (stat) => stat.activity === activity
    );
    
    if (!stat || !stat.total_time) {
      return '00:00 Hrs';
    }
    
    return `${stat.total_time} Hrs`;
  };

  const data1 = [
    {
      id: 1,
      text: "Average Working Time",
      total: getTimeStats("CHECKED"),
      icon: Clockicon,
    },
    {
      id: 2,
      text: "Total Break Time",
      total: getTimeStats("BREAK"),
      icon: CupIcon,
    },
    {
      id: 3,
      text: "Total Travel Time",
      total: getTimeStats("TRAVEL"),
      icon: CarsIcon,
    },
  ];

  const filteredData = (filter) => {
    if (!workerData?.chart) return [];
    
    const chartEntries = Object.entries(workerData.chart).map(([date, data]) => ({
      date: date,
      break: data.breaktime || '00:00',
      travel: data.travelTime || '00:00',
      workingHours: data.workingHours || '00:00'
    }));

    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const sixMonthsAgo = new Date(today.setMonth(today.getMonth() - 6));
    const startOfYear = new Date(today.getFullYear(), 0, 1);

    switch (filter) {
      case "This Week":
        return chartEntries.filter((data) => new Date(data.date) >= startOfWeek);
      case "This Month":
        return chartEntries.filter((data) => new Date(data.date) >= startOfMonth);
      case "Last 6 Months":
        return chartEntries.filter((data) => new Date(data.date) >= sixMonthsAgo);
      case "This Year":
        return chartEntries.filter((data) => new Date(data.date) >= startOfYear);
      default:
        return chartEntries;
    }
  };

  const chartData = filteredData(filter);

  const chartOptions = {
    title: {
      text: "Time Distribution",
      left: "center",
      top: "5%",
      textStyle: {
        fontSize: 18,
        fontWeight: "bold",
      },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#fff",
      borderColor: "#ccc",
      borderWidth: 1,
      textStyle: {
        color: "#333",
      },
      formatter: (params) => {
        let tooltipContent = `<div style="font-size: 14px; font-weight: bold;">${params[0].axisValue}</div>`;
        params.forEach((item) => {
          tooltipContent += `
            <div style="margin: 4px 0;">
              <span style="display: inline-block; width: 12px; height: 12px; background-color: ${item.color}; border-radius: 50%; margin-right: 5px;"></span>
              ${item.seriesName}: ${item.value} hrs
            </div>`;
        });
        return tooltipContent;
      },
    },
    legend: {
      data: ["Break", "Travel", "Working Hours"],
      bottom: "5%",
      textStyle: {
        fontSize: 12,
      },
    },
    grid: {
      top: "15%",
      bottom: chartData.length > 20 ? 80 : "20%",
      left: "5%",
      right: "5%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: chartData.map((entry) => entry.date),
      axisLabel: {
        fontSize: 11,
        // Let echarts decide tick density automatically
        interval: "auto",
        // Hide labels that would overlap and rotate when too many points
        hideOverlap: true,
        showMaxLabel: true,
        rotate: chartData.length > 20 ? 45 : 0,
        margin: 12,
        formatter: (value) => {
          const d = new Date(value);
          if (isNaN(d.getTime())) return value;
          return d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
        }
      },
      axisLine: {
        lineStyle: {
          color: "black",
        },
      },
      axisTick: {
        alignWithLabel: true,
      },
    },
    // Removed dataZoom slider as requested; axis labels already adjusted for density
    yAxis: {
      type: "value",
      name: "Hours",
      nameTextStyle: {
        fontSize: 14,
        fontWeight: "bold",
      },
      axisLine: {
        lineStyle: {
          color: "black",
        },
      },
      splitLine: {
        lineStyle: {
          type: "dashed",
          color: "#e0e0e0",
        },
      },
    },
    series: [
      {
        name: "Break",
        type: "line",
        data: chartData.map((entry) => entry.break),
        smooth: true,
        symbol: "circle",
        symbolSize: 8,
        lineStyle: {
          color: "#FF5733",
          width: 2,
        },
        itemStyle: {
          color: "#FF5733",
        },
      },
      {
        name: "Travel",
        type: "line",
        data: chartData.map((entry) => entry.travel),
        smooth: true,
        symbol: "triangle",
        symbolSize: 8,
        lineStyle: {
          color: "#33C3FF",
          width: 2,
        },
        itemStyle: {
          color: "#33C3FF",
        },
      },
      {
        name: "Working Hours",
        type: "line",
        data: chartData.map((entry) => entry.workingHours),
        smooth: true,
        symbol: "square",
        symbolSize: 8,
        lineStyle: {
          color: "#28B463",
          width: 2,
        },
        itemStyle: {
          color: "#28B463",
        },
      },
    ],
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const fetchWorker = async () => {
    try {
      const response = await getWorkerDetail(workerId);
      if (response?.success) {
        setWorkerData(response?.data);
      }
    } catch (error) {
      console.error("Error fetching worker:", error);
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    if (workerId) {
     
      fetchWorker();
    }
  }, [workerId]);

  useEffect(() => {
    const fetchExpenses = async () => {
      setExpenseLoading(true);
      try {
        const response = await getExpenses(1, 10, null, workerId);
        console.log('response expenses', response);
        if (response?.data) {
          setExpenses(response.data);
        }
      } catch (error) {
        console.error('Error fetching expenses:', error);
      } finally {
        setExpenseLoading(false);
      }
    };

    if (workerId) {
      fetchExpenses();
    }
  }, [workerId]);

  const handleOpenProjectSidebar = () => setProjectbarVisible(true);
  const handleCloseProjectSidebar = () => setProjectbarVisible(false);

  const canAccessFeature = (feature) => {
    const planLevels = {
      'trial': 0,
      'basic': 1,
      'premium': 2,
      'ultimate': 3
    };
    return planLevels[currentPlan] >= planLevels[FEATURE_REQUIRED_PLANS[feature]];
  };

  const handleRestrictedFeature = (featureName, requiredPlan) => {
    setRestrictedFeature({
      name: featureName,
      requiredPlan: requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)
    });
    setShowUpgradeModal(true);
  };

  const handleExpenseClick = () => {
    if (!canAccessFeature('expenseManagement')) {
      handleRestrictedFeature('Expense Management', 'premium');
      return;
    }
    navigate('/expenses', { state: { workerId } });
  };

  return (
    <div className="w-full flex flex-col gap-6 pb-12">
      <ListHeader
        mainHeading="Worker"
        placeholder="Search"
        btnShow={true}
        linkBreadCrum="/view-worker"
        blinkBreadCrumText="Worker"
        linkBreadCrum1="/view-worker"
        blinkBreadCrumText1="View Worker"
        blinkBreadCrumText2="Detail"
        searchShow={false}
        showLikeFilter={false}
        DropDown={false}
      />
{
  loader?<Spin/>:

  <>
  <div className="w-full grid grid-cols-3 gap-5">
    {data1?.map(({ id, text, total, icon }) => {
      return (
        <div key={id} className="w-full">
          <div className="rounded-[8px] w-full h-[100px] bg-white flex items-center justify-between">
            <div className="px-3 w-100 ">
              <div className="d-flex justify-content-between text-center align-items-center">
                <div className="dashboard_icon">{icon}</div>
                <div className="flex flex-col items-end">
                  <p className="font-medium !text-[28px] !text-[#4B5563]">
                    {total}
                  </p>
                  <p className="font-normal !text-[14px] !text-[#B7B7B7]">
                    {text}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
  <WorkerProfile
    workerData={workerData}
    handleOpenProjectSidebar={handleOpenProjectSidebar}
    handleOpen={handleOpen}
    setDeleteSuccess={handleClose}
    fetchWorker={fetchWorker}
    handleOpenReport={() => setIsReportModalOpen(true)}
  />
  <AddProjectSidebar
    visible={isProjectbarVisible}
    onClose={handleCloseProjectSidebar}
    fetchWorker={fetchWorker}
    workerId={workerId}
    workerData={workerData}
  />
     <Modal open={open} onClose={handleClose}>
    <DailyStatsCalendar
      onClose={handleClose}
      OpenStats={() => setIsStatsOpen(true)}
    />
  </Modal>
  <DailyStats
    open={isStatsOpen}
    onClose={() => setIsStatsOpen(false)}
    onBack={() => {
      setIsStatsOpen(false);
      setOpen(true);
    }}
    workerId={workerId}
  />

  {canAccessFeature('expenseManagement') && (
    <div className="w-full flex gap-5">
      {/* Left Panel: Expenses */}
      <div className="flex-1 h-[445px] flex flex-col gap-2 bg-white rounded-[8px] overflow-y-scroll">
        <div 
          className="w-full flex items-center justify-between pt-3 px-3 cursor-pointer hover:bg-gray-50"
          onClick={handleExpenseClick}
        >
          <div className="flex items-center gap-3">
            <p className="text-base font-semibold text-[#1E3A8A]">Expenses</p>
            <button className="w-auto min-w-[57px] h-[20px] bg-[#e4e7f1] rounded-[2px] mt-[6px] text-[10px] text-[#1E3A8A] font-medium px-2">
              ${expenses.reduce((total, expense) => total + expense.amount, 0)}
            </button>
          </div>
          <span>{NextArrow}</span>
        </div>
        
        {expenseLoading ? (
          <div className="flex items-center justify-center h-full">
            <p>Loading expenses...</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">No expenses found</p>
          </div>
        ) : (
          expenses.map((expense) => (
            <div
              key={expense.id}
              className="w-full flex items-center justify-between px-3 py-2 border-b border-b-[#EFEFEF] hover:bg-gray-50 rounded-lg"
            >
              <div className="flex flex-col">
                <span className="font-normal text-[14px] text-[#4B5563]">
                  {expense.title}
                </span>
                <span className="text-[12px] text-gray-500">
                  {new Date(expense.invoice_date).toLocaleDateString()}
                </span>
              </div>
              <p className="text-[14px] font-medium text-black">
                ${expense.amount}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Right Panel: Chart */}
      <div className="flex-1 h-[445px] bg-white rounded-[8px] flex flex-col">
        <div className="flex justify-end mt-2 px-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded px-4 py-2"
          >
            <option value="This Week">This Week</option>
            <option value="This Month">This Month</option>
            <option value="Last 6 Months">Last 6 Months</option>
            <option value="This Year">This Year</option>
            <option value="All Time">All Time</option>
          </select>
        </div>
        <ReactECharts option={chartOptions} style={{ flex: 1 }} />
      </div>
    </div>
  )}

  {/* <div className="mt-4">
    <h3 className="text-lg font-semibold mb-2">Live Location</h3>
    <SearchMap 
      workerId={workerId}
    />
  </div> */}

  <UpgradePromptModal
    show={showUpgradeModal}
    handleClose={() => setShowUpgradeModal(false)}
    featureName={restrictedFeature?.name}
    requiredPlan={restrictedFeature?.requiredPlan}
  />

  {/* Worker Hours Report Modal */}
  <WorkerHoursReport
    open={isReportModalOpen}
    onClose={() => setIsReportModalOpen(false)}
    preSelectedWorkerId={parseInt(workerId)}
  />
  </>
}
    </div>
  );
};

export default WorkerDetails;
